import {
  backUp,
  backupSettings,
  type BackupStatus,
  backupStatus,
  saveBackupSettings,
} from '@/app/backup/schedule';
import type { Note } from '@/app/db/repo';
import {
  type FormEvent,
  useEffect,
  useState,
} from 'react';
import { PasswordInput } from './PasswordInput';

const describe = (status: BackupStatus | null) => {
  if (!status) return 'No backup yet on this device.';
  const at = new Date(status.at).toLocaleString();
  return status.ok
    ? `Last backup ${at}, ${status.notes} notes.`
    : `Last backup failed ${at}.`;
};

// The daily backup's settings for this device, over the notes like the lock
// is. Saving sends one straight away, so a wrong secret shows at once.
export const Backup = (
  { notes, onClose }: {
    notes: () => Promise<Note[]>,
    onClose: () => void,
  },
) => {
  const saved = backupSettings();
  const [password, setPassword] = useState(saved?.password ?? '');
  const [secret, setSecret] = useState(saved?.secret ?? '');
  const [status, setStatus] = useState(backupStatus);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const settings = { password, secret: secret.trim() };
    saveBackupSettings(settings);
    setBusy(true);
    setStatus(await backUp(notes, settings));
    setBusy(false);
  };

  const turnOff = () => {
    saveBackupSettings(null);
    onClose();
  };

  return (
    <div className="backup" role="dialog" aria-label="Daily backup">
      <form className="centered form" onSubmit={submit}>
        <h1>Daily backup</h1>
        <p className="muted">{describe(status)}</p>
        <p>
          Once a day, while the app is open on this device, every note is
          emailed as a zip locked with the{' '}
          <em>backup password</em>. Open it with 7-Zip or Keka. The{' '}
          <em>backup secret</em> is the one set on the site's server.
        </p>
        <PasswordInput
          autoFocus
          autoComplete="new-password"
          placeholder="Backup password"
          aria-label="Backup password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
          minLength={12} />
        <PasswordInput
          autoComplete="off"
          placeholder="Backup secret"
          aria-label="Backup secret"
          value={secret}
          onChange={e => setSecret(e.target.value)}
          required />
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Backing up…' : saved ? 'Save and back up now' : 'Turn on'}
        </button>
        {status && !status.ok && (
          <p className="error" role="alert">{status.error}</p>
        )}
        {saved && (
          <button type="button" className="link" onClick={turnOff}>
            Turn off on this device
          </button>
        )}
        <button type="button" className="link" onClick={onClose}>
          Close
        </button>
      </form>
    </div>
  );
};
